import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import ScoreShareCard from '../components/ScoreShareCard';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, XCircle, Trophy, Clock, ArrowRight, Info, Sparkles, Brain, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const QuizResults = () => {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshUser } = useAuth();
  const results = location.state;

  const isPro = user?.subscription_status === 'active' || user?.role === 'admin';
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState('');
  const [status, setStatus] = useState(null);
  const [startingTrial, setStartingTrial] = useState(false);

  useEffect(() => {
    if (isPro) return;
    axios.get(`${API_URL}/api/subscriptions/status`).then(r => setStatus(r.data)).catch(() => {});
  }, [isPro]);

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/subscriptions/trial/start`);
      toast.success(data.message || 'Your 7-day Pro trial is live!');
      await refreshUser();
      // Optimistically refetch report
      setTimeout(() => fetchReport(), 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start trial');
    } finally {
      setStartingTrial(false);
    }
  };

  const fetchReport = async () => {
    setReportLoading(true);
    setReportError('');
    try {
      const { data } = await axios.get(`${API_URL}/api/profile/weakness-report`);
      setReport(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code === 'PRO_REQUIRED') {
        setReportError(detail.message || 'Pro subscription required');
      } else {
        setReportError(typeof detail === 'string' ? detail : 'Failed to load report');
      }
    } finally {
      setReportLoading(false);
    }
  };

  // If no results in state (e.g. direct URL access), redirect
  if (!results) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-[#6B7280] text-lg mb-4">No quiz results found.</p>
            <button
              onClick={() => navigate('/quiz-pools')}
              className="px-6 py-3 bg-[#0EA5E9] text-white rounded-2xl font-bold"
              data-testid="back-to-pools-button"
            >
              Back to Quiz Pools
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { score, total, percentage, time_taken, review } = results;
  const wrongCount = review ? review.filter(r => !r.is_correct).length : 0;

  const getScoreColor = () => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-[#FBBF24]';
    return 'text-[#FF6B6B]';
  };

  const getScoreBg = () => {
    if (percentage >= 80) return 'from-green-500 to-emerald-600';
    if (percentage >= 50) return 'from-[#FBBF24] to-[#F59E0B]';
    return 'from-[#FF6B6B] to-[#EE5A52]';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="quiz-results-page">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Score Summary Card */}
        <div className={`bg-gradient-to-r ${getScoreBg()} rounded-2xl p-6 sm:p-8 text-white mb-8 shadow-lg`} data-testid="score-summary">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <h1 className="text-3xl sm:text-4xl font-black mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Quiz Complete!
              </h1>
              <p className="text-white/80 text-sm sm:text-base">
                Here's how you did
              </p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-5xl sm:text-6xl font-black" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="score-display">
                  {score}/{total}
                </p>
                <p className="text-white/80 text-sm">Score</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="percentage-display">
                  {percentage}%
                </p>
                <p className="text-white/80 text-sm">Accuracy</p>
              </div>
              <div className="text-center">
                <div className="flex items-center gap-1">
                  <Clock className="w-5 h-5" />
                  <p className="text-2xl font-bold" data-testid="time-display">{time_taken}s</p>
                </div>
                <p className="text-white/80 text-sm">Time</p>
              </div>
            </div>
          </div>
        </div>

        {/* Score Share Card — encourages social sharing + referrals */}
        <div className="mb-8">
          <ScoreShareCard
            score={score}
            total={total}
            percentage={percentage}
            time_taken={time_taken}
            userName={user?.name || ''}
            subject={results?.subject || results?.pool_subject || ''}
            referralCode={user?.referral_code || ''}
            inviteUrl={user?.referral_code ? `https://aptivon.in/register?ref=${user.referral_code}` : 'https://aptivon.in'}
          />
        </div>

        {/* AI Weakness Report (Pro feature) */}
        <div className="mb-8 rounded-2xl border-2 overflow-hidden" data-testid="weakness-report-card" style={{ borderColor: isPro ? '#FBBF24' : '#E5E7EB' }}>
          <div className={`p-5 ${isPro ? 'bg-gradient-to-r from-amber-50 to-yellow-50' : 'bg-slate-50'}`}>
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${isPro ? 'bg-amber-400' : 'bg-slate-300'}`}>
                {isPro ? <Brain className="w-5 h-5 text-white" /> : <Lock className="w-5 h-5 text-white" />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base sm:text-lg font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                    AI Weakness Report
                  </h3>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${isPro ? 'bg-amber-400 text-white' : 'bg-slate-300 text-white'}`}>
                    Pro
                  </span>
                </div>
                <p className="text-sm text-[#6B7280]">
                  {isPro
                    ? 'Personalised insights from Claude Opus on what to revise next.'
                    : 'Personalised AI analysis of every wrong answer + 3 weak topics to focus on next.'}
                </p>
              </div>
            </div>

            {isPro ? (
              report ? (
                <div className="mt-4 space-y-3" data-testid="weakness-report-content">
                  <p className="text-sm text-[#374151] leading-relaxed">{report.report}</p>
                  {report.topics_to_improve && report.topics_to_improve.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-2">Topics to improve</p>
                      <ul className="space-y-1.5">
                        {report.topics_to_improve.map((t, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <span className="text-amber-600 font-bold">•</span>
                            <span><strong className="text-[#111827]">{t.topic}:</strong> <span className="text-[#6B7280]">{t.why}</span></span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {report.strengths && report.strengths.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-emerald-600 mb-2">Your strengths</p>
                      <ul className="space-y-1 text-sm text-[#374151]">
                        {report.strengths.map((s, i) => <li key={i}>✓ {s}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  onClick={fetchReport}
                  disabled={reportLoading}
                  className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold shadow-md text-sm transition disabled:opacity-60"
                  data-testid="generate-weakness-report-button"
                >
                  {reportLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Analysing your performance…</> : <><Sparkles className="w-4 h-4" /> Generate my AI report</>}
                </button>
              )
            ) : (
              <div className="mt-5 relative" data-testid="locked-report-preview">
                {/* Blurred SAMPLE report preview — clearly labelled, shows the format & value */}
                <div className="rounded-xl bg-white border border-slate-200 p-4 select-none pointer-events-none" style={{ filter: 'blur(5px)', userSelect: 'none' }} aria-hidden>
                  <p className="text-sm text-[#374151] mb-3">
                    Example report: the AI coach reviews every answer on your {results?.subject || 'quiz'}, pinpoints the exact concepts behind each mistake, and gives you a focused 3-topic revision plan for your next attempt.
                  </p>
                  <p className="text-xs font-bold uppercase tracking-wider text-[#9CA3AF] mb-2">Topics to fix first</p>
                  <ul className="space-y-1.5 text-sm">
                    <li className="flex items-start gap-2"><span className="text-amber-600 font-bold">•</span><strong>Weakest topic #1:</strong> <span className="text-[#6B7280]">why you're losing marks here</span></li>
                    <li className="flex items-start gap-2"><span className="text-amber-600 font-bold">•</span><strong>Weakest topic #2:</strong> <span className="text-[#6B7280]">the concept gap behind it</span></li>
                    <li className="flex items-start gap-2"><span className="text-amber-600 font-bold">•</span><strong>Weakest topic #3:</strong> <span className="text-[#6B7280]">exactly what to revise</span></li>
                  </ul>
                  <p className="text-xs font-bold uppercase tracking-wider text-emerald-600 mt-3 mb-1">Your strengths</p>
                  <p className="text-sm text-[#374151]">✓ Where you're already exam-ready</p>
                </div>

                {/* Unlock overlay */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-3">
                  <div className="bg-white/95 backdrop-blur-sm rounded-2xl px-5 py-4 shadow-xl border border-amber-200 max-w-sm">
                    <div className="flex items-center justify-center mb-2">
                      <span className="px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600" data-testid="sample-preview-badge">Sample preview</span>
                    </div>
                    <p className="text-sm font-bold text-[#111827] mb-1" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      Unlock your real AI report
                    </p>
                    <p className="text-xs text-[#6B7280] mb-3">
                      {wrongCount > 0
                        ? `Claude AI will analyse all ${wrongCount} of your wrong answer${wrongCount !== 1 ? 's' : ''} from this quiz and tell you exactly what to revise.`
                        : 'Claude AI analyses your performance across quizzes and tells you exactly what to revise next.'}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      {status?.trial_eligible && (
                        <button
                          onClick={handleStartTrial}
                          disabled={startingTrial}
                          className="px-4 py-2.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-black text-xs uppercase tracking-wide inline-flex items-center justify-center gap-1.5 shadow-lg shadow-amber-500/30 hover:shadow-xl transition disabled:opacity-60"
                          data-testid="trial-from-results-button"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          {startingTrial ? 'Activating…' : 'Try 7 days free'}
                        </button>
                      )}
                      <Link
                        to="/upgrade"
                        className={`px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wide inline-flex items-center justify-center gap-1.5 transition ${status?.trial_eligible ? 'bg-slate-100 text-[#374151] hover:bg-slate-200' : 'bg-gradient-to-r from-amber-400 to-orange-500 text-white shadow-lg'}`}
                        data-testid="upgrade-from-results-button"
                      >
                        See plans →
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {reportError && <p className="mt-3 text-xs text-red-500">{reportError}</p>}
          </div>
        </div>

        {/* Question Review */}
        <h2 className="text-xl font-black text-[#111827] mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
          Answer Review
        </h2>

        <div className="space-y-4 mb-8" data-testid="review-list">
          {review && review.map((item, idx) => (
            <div
              key={idx}
              className={`bg-white rounded-2xl border-2 p-5 sm:p-6 shadow-sm ${
                item.is_correct ? 'border-green-200' : 'border-red-200'
              }`}
              data-testid={`review-item-${idx}`}
            >
              {/* Question header */}
              <div className="flex items-start gap-3 mb-4">
                <div className={`mt-0.5 shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${
                  item.is_correct ? 'bg-green-100' : 'bg-red-100'
                }`}>
                  {item.is_correct
                    ? <CheckCircle className="w-5 h-5 text-green-600" />
                    : <XCircle className="w-5 h-5 text-red-500" />
                  }
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-[#6B7280] mb-1">Question {idx + 1}</p>
                  <p className="font-bold text-[#111827] text-sm sm:text-base">{item.question}</p>
                </div>
              </div>

              {/* Options */}
              <div className="space-y-2 ml-10 mb-4">
                {item.options.map((opt, optIdx) => {
                  const optLetter = opt.split(')')[0].trim().toUpperCase();
                  const isUserAnswer = optLetter === item.your_answer;
                  const isCorrect = optLetter === item.correct_answer;

                  let optClass = 'border-slate-100 bg-white text-[#374151]';
                  if (isCorrect) optClass = 'border-green-300 bg-green-50 text-green-800';
                  if (isUserAnswer && !isCorrect) optClass = 'border-red-300 bg-red-50 text-red-800';

                  return (
                    <div
                      key={optIdx}
                      className={`px-4 py-2.5 rounded-xl border-2 text-sm font-medium flex items-center gap-2 ${optClass}`}
                    >
                      <span>{opt}</span>
                      {isCorrect && <CheckCircle className="w-4 h-4 text-green-600 ml-auto shrink-0" />}
                      {isUserAnswer && !isCorrect && <XCircle className="w-4 h-4 text-red-500 ml-auto shrink-0" />}
                    </div>
                  );
                })}
              </div>

              {/* Explanation */}
              {item.explanation && (
                <div className="ml-10 px-4 py-3 bg-blue-50 border border-blue-100 rounded-xl">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-[#0EA5E9] mt-0.5 shrink-0" />
                    <p className="text-sm text-[#374151]">
                      <span className="font-bold text-[#0EA5E9]">Explanation: </span>
                      {item.explanation}
                    </p>
                  </div>
                </div>
              )}

              {/* Answer summary */}
              {!item.is_correct && (
                <div className="ml-10 mt-2 text-xs text-[#6B7280]">
                  Your answer: <span className="font-bold text-red-500">{item.your_answer || 'Skipped'}</span>
                  {' | '}
                  Correct: <span className="font-bold text-green-600">{item.correct_answer}</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate(`/leaderboard/${poolId}`)}
            className="flex items-center justify-center gap-2 px-8 py-3 bg-[#0EA5E9] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none transition-all"
            data-testid="view-leaderboard-button"
          >
            <Trophy className="w-5 h-5" />
            View Leaderboard
          </button>
          <button
            onClick={() => navigate('/quiz-pools')}
            className="flex items-center justify-center gap-2 px-8 py-3 bg-[#FBBF24] text-white rounded-2xl font-bold shadow-[0_4px_0_rgb(245,158,11)] active:translate-y-1 active:shadow-none transition-all"
            data-testid="more-quizzes-button"
          >
            Play More Quizzes
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </main>
    </div>
  );
};

export default QuizResults;
